import React, { useState } from 'react';
import {
  Collapse,
  Container,
  makeStyles,
  Paper,
  Typography,
} from '@material-ui/core';
import { ChevronLeft } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
  container: {
    height: 'fit-content',
    padding: theme.spacing(2),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    cursor: 'pointer',
    gap: theme.spacing(1),
  },
  dropdownToggle: {
    transitionProperty: 'all',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    transitionDuration: '500ms',
    rotate: '-90deg',
    transformStyle: 'preserve-3d',
  },
  rotate: {
    rotate: '-270deg',
  },
  loader: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    margin: theme.spacing(1),
  },
  pingDot: {
    position: 'relative',
    display: 'inline-block',
    width: 12,
    height: 12,
    borderRadius: '50%',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: '$ping 2s cubic-bezier(0, 0, 0.2, 1) infinite',
    },
  },
  green: {
    backgroundColor: '#10b981',
    '&::before': {
      backgroundColor: '#10b981',
    },
  },
  red: {
    backgroundColor: '#ef4444',
    '&::before': {
      backgroundColor: '#ef4444',
    },
  },
  yellow: {
    backgroundColor: '#fbbf24',
    '&::before': {
      backgroundColor: '#fbbf24',
    },
  },
  '@keyframes ping': {
    '0%': {
      transform: 'scale(1)',
      opacity: 1,
    },
    '75%, 100%': {
      transform: 'scale(2)',
      opacity: 0,
    },
  },
}));

/**
 * A collapsible container component that can expand/collapse its content with a click.
 *
 * @param {Object} props - Component props
 * @param {string} props.title - The title displayed in the header of the expandable section
 * @param {React.ReactNode} props.children - The content to be shown/hidden when expanded/collapsed
 * @param {boolean} [props.defaultToOpen=false] - Whether the component should be expanded on initial mount
 * @returns {React.ReactElement} A collapsible container with a clickable header and expandable content
 *
 * @example
 * <Expandable title="Settings" defaultOpen>
 *   <SettingsForm />
 * </Expandable>
 */
export const Expandable = ({
  title,
  children,
  active = false,
  showLiveUpdates = false,
  defaultToOpen = false,
  loading = false,
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(defaultToOpen);

  return (
    <Container component={Paper} className={classes.container}>
      <header
        className={classes.header}
        onClick={() => setOpen((prev) => !prev)}
      >
        {showLiveUpdates && !loading && active && (
          <span
            className={`${classes.pingDot} ${
              active ? classes.green : classes.red
            }`}
          />
        )}
        <Typography variant="h6">{title}</Typography>
        <ChevronLeft
          className={`${classes.dropdownToggle} ${open && classes.rotate}`}
        />
      </header>
      <Collapse in={open}>
        {!loading ? (
          children
        ) : (
          <section className={classes.loader}>
            <Skeleton animation="wave" />
            <Skeleton animation="pulse" />
            <Skeleton animation="wave" />
            <Skeleton animation="pulse" />
          </section>
        )}
      </Collapse>
    </Container>
  );
};
